/**
 * "Is photo recognition actually working, and if not, what do I go and fix?"
 *
 * This exists because of a bug that cost days. Recognition was switched off in
 * production by a build-time flag, and because the client never made a request,
 * nothing failed — there was no error in the console, no message on screen, and
 * no way to tell "off" from "broken" from "out of credits" without a laptop and
 * a phone debugger.
 *
 * Every one of those cases produces a different HTTP status. The app just wasn't
 * asking. So: one button, one real round trip, one sentence naming the thing to
 * fix — and the build stamp beside it, because the second most common cause of
 * "it doesn't work" is looking at a version that predates the fix.
 *
 * It sends a genuine 1×1 JPEG rather than a fake payload. A probe that takes a
 * different path from the real thing can pass while the real thing fails, which
 * would make this worse than having no check at all.
 */

import { BUILD } from "../../version";
import { readVisionConfig } from "./config";
import { failureForReply } from "./recogniseFood";
import type { RecognitionFailure } from "./recogniseFood";

/** A real, minimal JPEG. Small enough to be free, valid enough to be decoded. */
const PROBE_PHOTO =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsL" +
  "DBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/wAALCAABAAEBAREA/8QAFAAB" +
  "AAAAAAAAAAAAAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/2gAIAQEAAD8AKp//2Q==";

/** How long to wait before calling it unreachable. Shorter than a real scan. */
const TIMEOUT_MS = 15_000;

/**
 * A second probe, the size of a real receipt — and a **real image**.
 *
 * The 1×1 probe proves the route, the key and the credits. It proves all three
 * at 800 bytes, and a receipt is ~150KB, so it cannot see anything that breaks
 * in between.
 *
 * The first attempt at this padded the 1×1 probe's base64 out to 150,000
 * characters by repeating it. That is valid base64 and **not a decodable
 * image**, so the far end rejected it for being corrupt — and the check
 * reported "works small, fails large", which reads exactly like a size limit
 * and was nothing of the kind. A diagnostic that produces a confident wrong
 * cause is worse than no diagnostic, so this one now draws real noise on a
 * canvas and encodes a real JPEG, taking the same path a real photo takes.
 *
 * Returns null where there is no canvas to draw on — a check must never report
 * a failure it cannot tell apart from its own limitations.
 */
const LARGE_PROBE_TARGET = 120_000;
const LARGE_PROBE_CEILING = 170_000;

async function largeProbe(): Promise<string | null> {
  try {
    /*
     * Noise, because noise is what makes a photograph big — it is the part JPEG
     * cannot compress, and why the same receipt is 123KB rendered and 392KB
     * photographed.
     *
     * The edges go up in small steps and stop at the first that clears the
     * target. They used to start at 500, which produces **212KB** — a probe
     * meant to be receipt-sized was half again as big as a receipt, so it
     * tested a payload nobody ever sends and spent the allowance doing it.
     */
    for (const edge of [340, 380, 420, 470, 530, 600]) {
      const canvas = document.createElement("canvas");
      canvas.width = edge;
      canvas.height = edge;
      const context = canvas.getContext("2d");
      if (!context) return null;

      const pixels = context.createImageData(edge, edge);
      for (let i = 0; i < pixels.data.length; i += 4) {
        pixels.data[i] = Math.random() * 256;
        pixels.data[i + 1] = Math.random() * 256;
        pixels.data[i + 2] = Math.random() * 256;
        pixels.data[i + 3] = 255;
      }
      context.putImageData(pixels, 0, 0);

      const url = canvas.toDataURL("image/jpeg", 0.8);
      // Big enough to be the test, not so big it becomes a different one.
      if (url.length >= LARGE_PROBE_TARGET) {
        return url.length <= LARGE_PROBE_CEILING ? url : null;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export type VisionCheck =
  | { status: "off"; detail: string; report: string }
  | { status: "ok"; detail: string; report: string }
  /** The small probe worked and the receipt-sized one didn't. */
  | { status: "small-only"; detail: string; report: string }
  | {
      status: "failed";
      reason: RecognitionFailure;
      httpStatus: number | null;
      detail: string;
      report: string;
    };

/**
 * One line holding every fact, for pasting somewhere.
 *
 * This exists because three rounds of "it doesn't work" were spent on
 * paraphrased verdicts. A summary the user can copy exactly removes a step
 * where information gets lost, and it carries the build stamp — because the
 * second most common cause of "it doesn't work" is running a version that
 * predates the fix.
 */
function report(parts: Record<string, string | number | null | undefined>): string {
  return Object.entries(parts)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${value}`)
    .join(" ");
}

/**
 * What to actually do about each outcome. Deliberately concrete — "contact your
 * administrator" is not a sentence anyone has ever been helped by, and on this
 * project the administrator is Spencer.
 */
const REMEDIES: Record<RecognitionFailure, string> = {
  "not-configured":
    "The endpoint is live but has no key. Run: npx wrangler pages secret put NVIDIA_API_KEY — then redeploy.",
  "no-route":
    "Nothing is answering /api/vision. On Cloudflare this means the Pages Function didn't deploy — check that a functions/ directory sits beside dist/ when you run wrangler pages deploy.",
  unauthorised:
    "The key was rejected. It may have been rotated or revoked; generate a new one at build.nvidia.com and set it again.",
  "no-credits":
    "The NVIDIA account has no credits left. The free tier is a fixed allowance, not a monthly one — request more on the NVIDIA developer forum, or point VISION_MODEL at a different provider.",
  "model-unavailable":
    "NVIDIA answered 404 'Function not found for account'. The model is retired, or fenced out of your region — Meta's Llama 3.2 vision licence blocks the EU. Set a different one: npx wrangler pages secret put VISION_MODEL (try nvidia/nemotron-nano-12b-v2-vl, which has no regional restriction), then redeploy.",
  "rate-limited":
    "You've hit the limit — 30 photos an hour per client. Wait, or bind a KV namespace for a durable counter.",
  unreachable:
    "The request never left, or never arrived. Check you're online, and that the site is being served over HTTPS.",
  timeout: "The model took more than 15 seconds. Usually a cold start — try once more.",
  "server-error": "The photo service returned an error it didn't explain. Try again shortly.",
  unreadable:
    "The round trip worked and the key is fine — the model just didn't answer in the shape we asked for. Recognition will be unreliable but not dead.",
};

/**
 * Runs one real request and reports what came back.
 *
 * Never throws: this is the thing you reach for when something is already
 * wrong, and a diagnostic that can itself fail is not one.
 */
/** One request. Null means it threw or was aborted. */
async function send(
  config: NonNullable<ReturnType<typeof readVisionConfig>>,
  photo: string,
  signal: AbortSignal
): Promise<Response | null> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (config.apiKey) headers.Authorization = `Bearer ${config.apiKey}`;
  try {
    return await fetch(config.endpoint, {
      method: "POST",
      signal,
      headers,
      // The large probe asks for one token: the question is whether the
      // payload survives the trip, not what the model makes of noise.
      body: JSON.stringify({ photo, task: photo.length > 50_000 ? "probe" : undefined }),
    });
  } catch {
    return null;
  }
}

export async function checkVision(): Promise<VisionCheck> {
  const config = readVisionConfig();
  if (!config) {
    return {
      status: "off",
      detail: "Recognition is switched off in this build (VITE_VISION_PROXY=0).",
      report: report({ build: BUILD, verdict: "off" }),
    };
  }

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TIMEOUT_MS);

  try {
    const response = await send(config, PROBE_PHOTO, controller.signal);
    if (response === null) {
      const reason: RecognitionFailure = timedOut ? "timeout" : "unreachable";
      return {
        status: "failed",
        reason,
        httpStatus: null,
        detail: REMEDIES[reason],
        report: report({ build: BUILD, verdict: reason, mode: config.mode, probe: "small" }),
      };
    }

    if (!response.ok) {
      const problem: unknown = await response.json().catch(() => undefined);
      const reason = failureForReply(response.status, problem);
      const upstream = (problem as { upstreamStatus?: number } | undefined)?.upstreamStatus;
      return {
        status: "failed",
        reason,
        // The status worth showing is the one that diagnoses. When the far end
        // refused, ours is always 502 and theirs is the interesting number.
        httpStatus: upstream ?? response.status,
        detail: REMEDIES[reason],
        report: report({
          build: BUILD,
          verdict: reason,
          mode: config.mode,
          small: response.status,
          upstream,
        }),
      };
    }

    // A 200 proves the chain: route deployed, key present, key accepted,
    // credits available, model reachable. What the model *said* about a 1×1
    // pixel is meaningless, so it isn't checked.
    //
    // But it proves it only at 800 bytes, and receipt scanning sends 150KB.
    // The second probe is the one that catches what the first cannot.
    const photo = await largeProbe();
    if (photo === null) {
      // No canvas, or it wouldn't encode. Say what was actually checked rather
      // than inventing a verdict about what wasn't.
      return {
        status: "ok",
        detail:
          `Reached the model with a small photo (${config.mode} mode). ` +
          `Couldn't build a receipt-sized test image here, so receipt scanning ` +
          `is untested.`,
        report: report({ build: BUILD, verdict: "small-ok", mode: config.mode, large: "unbuilt" }),
      };
    }

    const sizeKb = Math.round(photo.length / 1024);
    const large = await send(config, photo, controller.signal);
    if (large !== null && !large.ok) {
      return {
        status: "small-only",
        detail:
          `A small photo works, but a ${sizeKb}KB one came back ${large.status}. ` +
          `Recognition and shelf scanning are fine; receipt scanning is what ` +
          `will fail. ${REMEDIES[failureForReply(large.status)]}`,
        report: report({
          build: BUILD,
          verdict: "small-only",
          mode: config.mode,
          largeKb: sizeKb,
          largeStatus: large.status,
        }),
      };
    }
    if (large === null) {
      return {
        status: "small-only",
        detail:
          `A small photo works, but a ${sizeKb}KB one never came back. ` +
          `Recognition and shelf scanning are fine; receipt scanning is what ` +
          `will time out.`,
        report: report({
          build: BUILD,
          verdict: "small-only",
          mode: config.mode,
          largeKb: sizeKb,
          largeStatus: "no-reply",
        }),
      };
    }

    return {
      status: "ok",
      detail: `Reached the model with both a small and a ${sizeKb}KB photo (${config.mode} mode).`,
      report: report({ build: BUILD, verdict: "ok", mode: config.mode, largeKb: sizeKb }),
    };
  } catch {
    const reason: RecognitionFailure = timedOut ? "timeout" : "unreachable";
    return {
      status: "failed",
      reason,
      httpStatus: null,
      detail: REMEDIES[reason],
      report: report({ build: BUILD, verdict: reason, mode: config.mode }),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The large probe, exposed for the browser suite.
 *
 * "Is this a decodable image" is a question only a decoder can answer, so it is
 * asked in a real browser. Exported rather than duplicated, because a copy of
 * the probe in a test would have passed happily while the real one sent
 * corrupt data.
 */
export const __largeProbeForTest = largeProbe;
